/**
 * Slug normalization shared between manifest assembly (build time) and
 * URL → article lookup (runtime). Applied at BOTH ends so that
 * case-insensitive macOS dev never diverges from case-sensitive CI:
 * a file named `Insights/Chat.mdx` and a URL `/docs/insights/chat`
 * must round-trip through the same canonical form.
 */
export function normalizeSlug(s: string): string {
  return s.trim().toLowerCase().replace(/^\/+/, "").replace(/\/+$/, "");
}

/**
 * Convert an `import.meta.glob` key (e.g. `/docs/insights/chat/overview.mdx`)
 * to a normalized slug (`insights/chat/overview`). Strips the `/docs/`
 * prefix, the `.mdx` extension, and any `/index` suffix.
 */
export function pathToSlug(globPath: string): string {
  return normalizeSlug(
    globPath
      .replace(/^\/docs\//, "")
      .replace(/\.mdx$/i, "")
      .replace(/\/index$/i, "")
  );
}

/**
 * True when a docs path belongs to the drawer-only `_help/` surface — a
 * reserved per-section subfolder whose MDX renders only inside the on-page
 * help drawer, never in the /docs sidebar/search/routes (see ADR-0069).
 *
 * This is the SINGLE source of truth for the docs-vs-help split, imported
 * by the runtime manifest AND both build scripts (validate-docs,
 * check-doc-links) so the two surfaces can never drift. It matches a
 * separator-bounded `_help` path segment, so it is correct for BOTH Vite
 * glob keys (`/docs/.../_help/x.mdx`) and Node absolute fs paths
 * (`/Users/.../docs/.../_help/x.mdx`), and does NOT match decoys like
 * `_helper/` or `a_help/`.
 */
export function isHelpPath(p: string): boolean {
  return /(?:^|\/)_help(?:\/|$)/.test(p);
}
